import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { FiPlus, FiEdit2, FiTrash2 } from 'react-icons/fi';
import {
  fetchCities,
  createCity,
  updateCity,
  deleteCity,
  resetSuccess,
} from '../../store/slices/citiesSlice';
import { fetchStates } from '../../store/slices/statesSlice';
import { DataTable, Modal, ConfirmDialog, SearchBox } from '../../components/Common';
import { getStatusBadgeClass, getStatusText } from '../../utils/helpers';

const Cities = () => {
  const dispatch = useDispatch();
  const { cities, isLoading, success, error } = useSelector((state) => state.cities);
  const { states } = useSelector((state) => state.states);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [formData, setFormData] = useState({ name: '', stateId: '', status: true });
  const [searchTerm, setSearchTerm] = useState('');
  const [filterState, setFilterState] = useState('');

  useEffect(() => {
    dispatch(fetchCities());
    dispatch(fetchStates());
  }, [dispatch]);

  useEffect(() => {
    if (success) {
      toast.success(selectedItem ? 'Updated successfully!' : 'Created successfully!');
      dispatch(resetSuccess());
      closeModal();
    }
  }, [success, dispatch, selectedItem]);

  useEffect(() => {
    if (error) {
      toast.error(error);
    }
  }, [error]);

  const openModal = (item = null) => {
    if (item) {
      setSelectedItem(item);
      setFormData({
        name: item.name,
        stateId: item.stateId?._id || item.stateId,
        status: item.status,
      });
    } else {
      setSelectedItem(null);
      setFormData({ name: '', stateId: '', status: true });
    }
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedItem(null);
    setFormData({ name: '', stateId: '', status: true });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.name || !formData.stateId) {
      toast.error('Please fill all required fields');
      return;
    }

    if (selectedItem) {
      dispatch(updateCity({ id: selectedItem._id, data: formData }));
    } else {
      dispatch(createCity(formData));
    }
  };

  const handleDelete = () => {
    if (selectedItem) {
      dispatch(deleteCity(selectedItem._id)).then((result) => {
        if (deleteCity.fulfilled.match(result)) {
          toast.success('Deleted successfully!');
          setIsDeleteOpen(false);
          setSelectedItem(null);
        }
      });
    }
  };

  const filteredCities = cities.filter((city) => {
    const matchesSearch = city.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesState = !filterState || (city.stateId?._id || city.stateId) === filterState;
    return matchesSearch && matchesState;
  });

  const columns = [
    {
      header: 'City Name',
      accessor: 'name',
    },
    {
      header: 'State',
      render: (row) => row.stateId?.name || '-',
    },
    {
      header: 'Status',
      render: (row) => (
        <span className={`badge ${getStatusBadgeClass(row.status)}`}>
          {getStatusText(row.status)}
        </span>
      ),
    },
    {
      header: 'Actions',
      render: (row) => (
        <div className="table-actions">
          <button
            className="btn btn-secondary btn-icon"
            onClick={() => openModal(row)}
            title="Edit"
          >
            <FiEdit2 />
          </button>
          <button
            className="btn btn-danger btn-icon"
            onClick={() => {
              setSelectedItem(row);
              setIsDeleteOpen(true);
            }}
            title="Delete"
          >
            <FiTrash2 />
          </button>
        </div>
      ),
      style: { width: '120px' },
    },
  ];

  return (
    <div>
      <div className="card">
        <div className="card-header">
          <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
            <h3 className="card-title">Cities</h3>
            <SearchBox
              value={searchTerm}
              onChange={setSearchTerm}
              placeholder="Search cities..."
            />
            <select
              className="form-control form-select"
              style={{ width: '200px' }}
              value={filterState}
              onChange={(e) => setFilterState(e.target.value)}
            >
              <option value="">All States</option>
              {states.map((state) => (
                <option key={state._id} value={state._id}>
                  {state.name}
                </option>
              ))}
            </select>
          </div>
          <button className="btn btn-primary" onClick={() => openModal()}>
            <FiPlus /> Add City
          </button>
        </div>
        <div className="card-body" style={{ padding: 0 }}>
          <DataTable
            columns={columns}
            data={filteredCities}
            isLoading={isLoading}
            emptyMessage="No cities found"
          />
        </div>
      </div>

      <Modal
        isOpen={isModalOpen}
        onClose={closeModal}
        title={selectedItem ? 'Edit City' : 'Add City'}
      >
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">State *</label>
            <select
              className="form-control form-select"
              value={formData.stateId}
              onChange={(e) => setFormData({ ...formData, stateId: e.target.value })}
            >
              <option value="">Select State</option>
              {states.map((state) => (
                <option key={state._id} value={state._id}>
                  {state.name}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label className="form-label">City Name *</label>
            <input
              type="text"
              className="form-control"
              placeholder="Enter city name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            />
          </div>

          <div className="form-group">
            <label className="form-label">Status</label>
            <select
              className="form-control form-select"
              value={formData.status}
              onChange={(e) => setFormData({ ...formData, status: e.target.value === 'true' })}
            >
              <option value="true">Active</option>
              <option value="false">Inactive</option>
            </select>
          </div>

          <div className="modal-footer" style={{ padding: '16px 0 0', borderTop: 'none' }}>
            <button type="button" className="btn btn-secondary" onClick={closeModal}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={isLoading}>
              {isLoading ? 'Saving...' : selectedItem ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </Modal>

      <ConfirmDialog
        isOpen={isDeleteOpen}
        onClose={() => setIsDeleteOpen(false)}
        onConfirm={handleDelete}
        title="Delete City"
        message={`Are you sure you want to delete "${selectedItem?.name}"? This action cannot be undone.`}
        isLoading={isLoading}
      />
    </div>
  );
};

export default Cities;
